import React, { useContext, useEffect, useState, Suspense } from "react";

// MUI Components
import {
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  TextField,
  InputAdornment,
  Stack,
  Autocomplete,
  Avatar,
  Chip,
  Badge,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";

// React Router
import { NavLink, useLocation, useNavigate } from "react-router-dom";

// Contexts
import { useAuthContext } from "./context/AuthContext";
import { SnackBarContext } from "./context/SnackBarContext";
import { useTranslation } from "./context/Internationalization";

// Hooks
import useGraphql from "./Hooks/useGraphql";
import useLog from "./Hooks/useLog";

// GraphQL Queries
import { GET_ME } from "./graphql/userGraphql";
import { GET_PROFILES } from "./graphql/profileGraphql";

// Components
import Loader from "./Components/Loader";
import TranslationToggle from "./Components/TranslationToggle";
import TopBar from "./Components/TopBar/TopBar";
import LogoutConfirmationModal from "./Components/LogoutConfirmationModal";
import ClubDetachConfirmationModal from "./Components/ClubDetachConfirmationModal";

// Utilities
import { getProfilesTeamDetails } from "./utility/Utils";
import { clearCookies, clearStorage, getStorage, signOut } from "./auth/utils";
import { localMap } from "./utility/Enum";

// Assets - Images & Icons
import playerDexLogo from "./assets/images/admin-icon-2/Content.png";
import playerDex from "./assets/images/admin-icon-2/playerdex.png";
import HomeIcon from "./assets/images/icons/home.png";
import HomeSelectedIcon from "./assets/images/icons/home_selected.png";
// import ScheduleIcon from "./assets/images/icons/schedule.png";
// import ScheduleSelectedIcon from "./assets/images/icons/schedule_selected.png";
import ClubIcon from "./assets/images/icons/club.png";
import ClubSelectedIcon from "./assets/images/icons/club_selected.png";
import LeagueIcon from "./assets/images/admin-icon/Menu-Icons-2.png";
import LeagueSelectedIcon from "./assets/images/admin-icon/Menu-Icons-3.png";
import FieldIcon from "./assets/images/icons/schedule.png";
import FieldSelectedIcon from "./assets/images/icons/schedule_selected.png";
import BanReportsIcon from "./assets/images/admin-icon/Essentials-1.png";
import BanReportsSelectedIcon from "./assets/images/admin-icon/Essentials.png";
import LogoutIcon from "./assets/images/Svg/LogoutIcon";

// Theme
import { colors } from "./theme/colors";
import { fonts } from "./theme/fonts";

const { Grey, Base } = colors;

const {
  text: { md, sm },
} = fonts;

const drawerWidth = 312;

const MenuList = ({ menu, t, pathname, style, closeDrawerMobile }) => {
  return (
    <List style={style}>
      {menu(t).map((item, index) => {
        if (item.divider) {
          return (
            <div
              key={"divider" + index}
              style={{ borderTop: "1px solid #1F2831", margin: "16px" }}
            />
          );
        }

        const isActive = pathname.includes(item.path) || item.drawerOpened;

        return (
          <ListItem key={index} disablePadding>
            <ListItemButton
              onClick={item.onClick ? () => item.onClick() : undefined}
              style={{ paddingLeft: "24px", justifyContent: "space-between" }}
            >
              <div style={{ alignItems: "center", display: "flex" }}>
                <ListItemIcon style={{ minWidth: "38px" }}>
                  {item.icon(isActive)}
                </ListItemIcon>

                <NavLink
                  style={isActive ? styles.selectedLink : styles.link}
                  to={item.path}
                  onClick={closeDrawerMobile}
                >
                  {item.label}
                </NavLink>
              </div>
              {item.badge && (
                <Chip
                  style={{ height: 22 }}
                  label={item.badge}
                  color={"primary"}
                />
              )}
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};

const AppShell = ({ window, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [detachClubModal, setDetachClubModal] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState("");
  const { t, switchLanguage, lang } = useTranslation();
  const { pathname } = useLocation();
  const auth = useAuthContext();
  const club =
    auth.authState.club && Object.entries(auth.authState.club).length > 0
      ? auth.authState.club
      : null;
  const clubId = club?._id ?? 0;
  const user = auth.authState.user;
  const snackBar = useContext(SnackBarContext);
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  const isDev = process.env.REACT_APP_NODE_ENV === "development";

  const getUser = () => {
    axiosGraphQL({ query: GET_ME })
      .then(async (response) => {
        const user = response.data.data.me?.user;
        if (!user) {
          auth.logout();
          return;
        }

        const userLangDB = user.language;
        const userLangLS = getStorage().getItem("language");
        if (userLangDB !== userLangLS) {
          switchLanguage(userLangDB);
        }

        const club = JSON.parse(getStorage().getItem("club"));
        club
          ? auth.setAuthState({
              user,
              token: getStorage().getItem("token"),
              club,
            })
          : auth.setAuthState({
              user,
              token: getStorage().getItem("token"),
            });

        const isValidRole = user?.isAdmin;
        if (!isValidRole) {
          auth.logout();
        }
      })
      .catch((error) => {
        errorLog(error, "AppShell", "getUser", "GET_ME");
        snackBar.setOptions({
          alertMessage: error.message,
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
      });
  };

  useEffect(() => {
    getUser();
  }, []);

  let clubMenuLinks = (t) =>
    [
      {
        label: t("club_page_title"),
        path: "/club/teams",
        icon: (isSelected) => (
          <img
            src={isSelected ? ClubSelectedIcon : ClubIcon}
            style={{ width: 24, height: 24 }}
            alt={"club-icon"}
          />
        ),
      },
    ].filter(Boolean);

  let superAdminMenuLinks = (t) => {
    return [
      { divider: true },
      {
        label: t("posts"),
        path: "/super-admin/stories",
        icon: (isSelected) => (
          <img
            src={isSelected ? HomeSelectedIcon : HomeIcon}
            style={{ width: 24, height: 24 }}
            alt={"story-icon"}
          />
        ),
      },
      {
        label: t("clubs_admin"),
        path: "/super-admin/clubs",
        icon: (isSelected) => (
          <img
            src={isSelected ? ClubSelectedIcon : ClubIcon}
            style={{ width: 24, height: 24 }}
            alt={"club-icon"}
          />
        ),
      },
      {
        label: t("official_leagues"),
        path: "/super-admin/official-leagues",
        icon: (isSelected) => (
          <img
            src={isSelected ? LeagueSelectedIcon : LeagueIcon}
            style={{ width: 24, height: 24 }}
            alt={"league-icon"}
          />
        ),
      },
      {
        label: t("fields"),
        path: "/super-admin/fields",
        icon: (isSelected) => (
          <img
            src={isSelected ? FieldSelectedIcon : FieldIcon}
            style={{ width: 24, height: 24 }}
            alt={"field-icon"}
          />
        ),
      },
      {
        label: t("ban_reports"),
        path: "/super-admin/ban-reports",
        icon: (isSelected) => (
          <img
            src={isSelected ? BanReportsSelectedIcon : BanReportsIcon}
            style={{ width: 24, height: 24 }}
            alt={"club-icon"}
          />
        ),
      },
      { divider: true },
    ];
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      // Sign out from Firebase
      await signOut();

      // clear lS and SS
      clearStorage();

      // clear cookies
      clearCookies();

      // Perform logout via auth context
      auth.logout();

      // Close any open drawer
      closeDrawerMobile();
    } catch (error) {
      snackBar.setOptions({
        alertMessage: t("error_during_logout"),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    if (search.length >= 3) {
      axiosGraphQL({
        query: GET_PROFILES,
        variables: {
          term: search,
          clubId,
          skip: 0,
          limit: 500,
        },
      })
        .then(async (response) => {
          const players = response.data.data.getPlayers || [];

          let teamDetails = await getProfilesTeamDetails(
            clubId,
            axiosGraphQL,
            "AppShell",
            snackBar,
            errorLog
          );
          const playersOptions = [];
          for (const player of players) {
            for (const team of player?.teams) {
              if (teamDetails[team]) {
                playersOptions.push({
                  label: `${player?.firstName} ${player?.lastName} (${teamDetails[team]?.name})`,
                  value: player?._id,
                  teamId: teamDetails[team]?._id,
                });
              } else {
                playersOptions.push({
                  label: `${player?.firstName} ${player?.lastName}`,
                  value: player?._id,
                });
              }
            }
          }
          setPlayers(playersOptions);
        })
        .catch((error) => {
          errorLog(error, "AppShell", "useEffect", "GET_PROFILES");
          snackBar.setOptions({
            alertMessage: error.message,
            alertSeverity: "error",
          });
          snackBar.setSnackbarOpen(true);
        });
    }
  }, [search]);

  const handleSearch = ({ target: { value } }) => {
    setSearch(value);
  };
  const navigate = useNavigate();

  const handleOnClick = (e, value) => {
    if (value?.value) {
      if (value?.teamId) navigate(`/players/${value?.teamId}/${value?.value}`);
      else navigate(`/players/${value?.value}`);
    }
  };

  const resetSearch = () => {
    setSearch("");
    setPlayers([]);
  };

  const closeDrawerMobile = () => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const handleDetachClub = async () => {
    try {
      auth.setAuthState({
        user: { ...user },
        token: getStorage().getItem("token"),
        club: undefined, // triggers localStorage cleanup
      });
      getStorage().removeItem("club");

      snackBar.setOptions({
        alertMessage: t("club_detached_successfully"),
        alertSeverity: "success",
      });
      snackBar.setSnackbarOpen(true);
      setDetachClubModal(false);
      setMobileOpen(false);
      navigate("/super-admin/clubs");
    } catch (error) {
      errorLog(error, "AppShell", "handleDetachClub", "setAuthState");
      snackBar.setOptions({
        alertMessage: t("error_detaching_club"),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
    }
  };

  const drawer = (
    <div>
      <Stack
        sx={{ marginTop: "32px", marginLeft: "24px" }}
        direction={"row"}
        spacing={1}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          sx={{ display: { xs: "none", lg: "flex" } }}
        >
          <Badge
            invisible={!isDev}
            color="error"
            badgeContent="DEV"
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.55rem",
                fontWeight: "bold",
                padding: "0 4px",
                height: "14px",
                minWidth: "22px",
                borderRadius: "4px",
              },
            }}
          >
            <img
              style={{ width: "32px", height: "32px" }}
              src={playerDexLogo}
              alt="PlayerDex logo"
            />
          </Badge>
          <img
            style={{
              width: "117px",
              height: "12px",
              display: "inline-block",
              marginLeft: "8px",
            }}
            src={playerDex}
            alt="PlayerDex"
          />
        </Stack>
        {!mobileOpen ? <TranslationToggle /> : null}
      </Stack>
      <Divider sx={{ display: { xs: "none", lg: "block" } }} />
      {club ? (
        <Stack
          direction={"row"}
          alignItems={"center"}
          style={styles.searchFieldContainer}
          sx={{ display: { xs: "none", lg: "flex" } }}
        >
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            options={players}
            fullWidth
            value={""}
            noOptionsText={""}
            onChange={handleOnClick}
            onInputChange={(_event, _value, reason) => {
              if (reason === "clear") {
                resetSearch();
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                onChange={handleSearch}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    style: styles.searchField,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: Grey[500] }} />
                      </InputAdornment>
                    ),
                  },
                }}
                placeholder={t("search")}
                fullWidth
              />
            )}
          />
        </Stack>
      ) : null}
      {club ? (
        <Stack
          direction={"row"}
          alignItems={"center"}
          style={styles.searchFieldContainer}
        >
          <TextField
            fullWidth
            name={"selectClub"}
            defaultValue={club?.name}
            slotProps={{
              input: {
                readOnly: true,
                style: { ...styles.clubsField, cursor: "initial" },
                startAdornment: (
                  <InputAdornment position="start">
                    <Avatar
                      alt={club?.name}
                      sx={{ width: 24, height: 24 }}
                      src={club?.logo || club?.name?.substring(0, 1)}
                    />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Tooltip title={t("detach_club")}>
            <IconButton
              style={styles.detachIcon}
              onClick={() => setDetachClubModal(true)}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : null}
      {club ? (
        <MenuList
          menu={clubMenuLinks}
          t={t}
          pathname={pathname}
          closeDrawerMobile={closeDrawerMobile}
        />
      ) : null}
      <MenuList
        menu={superAdminMenuLinks}
        t={t}
        pathname={pathname}
        closeDrawerMobile={closeDrawerMobile}
      />
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const onLogout = () => {
    setLogoutModal(true);
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={localMap[lang]}
    >
      <Box
        sx={{
          display: "flex",
          paddingBottom: "56px",
          paddingTop: { xs: "90px", lg: 0 },
        }}
      >
        <CssBaseline />
        <TopBar
          handleDrawerToggle={handleDrawerToggle}
          handleLogout={onLogout}
          handleOnClick={handleOnClick}
          handleSearch={handleSearch}
          resetSearch={resetSearch}
          players={players}
          user={user}
        />
        <Box
          component="nav"
          sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}
          aria-label="navigator"
        >
          {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: "block", lg: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                backgroundColor: "#0D151A",
              },
            }}
          >
            {drawer}
            <div style={{ height: "100%" }} />
            <LogoutConfirmationModal
              logout={handleLogout}
              open={logoutModal}
              onClose={() => setLogoutModal(false)}
            />
            <div style={{ padding: "0 16px" }}>
              <List style={{ borderTop: "1px solid #1F2831" }}>
                <ListItem key={"logout"} disablePadding onClick={onLogout}>
                  <ListItemButton>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      style={{ width: "100%" }}
                      justifyContent={"space-between"}
                      spacing={1}
                    >
                      <Stack direction={"row"} spacing={1}>
                        <ListItemIcon style={{ minWidth: "38px" }}>
                          {user?.photo ? (
                            <Avatar src={user?.photo} alt="user photo" />
                          ) : (
                            <Avatar src={user?.photo} alt="user photo">
                              {`${user?.firstName?.substring(
                                0,
                                1
                              )}${user?.lastName?.substring(0, 1)}`}
                            </Avatar>
                          )}
                        </ListItemIcon>
                        <Stack direction={"column"} spacing={0}>
                          <span style={styles.userName}>
                            {user?.firstName} {user?.lastName}
                          </span>
                          <span style={styles.userTitle}>
                            {`${t("administrator")} PlayerDex`}
                          </span>
                        </Stack>
                      </Stack>
                      <Tooltip title={t("logout")}>
                        <div>
                          <LogoutIcon />
                        </div>
                      </Tooltip>
                    </Stack>
                  </ListItemButton>
                </ListItem>
              </List>
            </div>
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", lg: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                backgroundColor: Base.Black,
              },
            }}
            open
          >
            {drawer}
            <div style={{ height: "100%" }} />
            <LogoutConfirmationModal
              logout={handleLogout}
              open={logoutModal}
              onClose={() => setLogoutModal(false)}
            />
            <div style={{ padding: "0 16px" }}>
              <List style={{ borderTop: "1px solid #1F2831" }}>
                <ListItem key={"logout"} disablePadding onClick={onLogout}>
                  <ListItemButton>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      style={{ width: "100%" }}
                      justifyContent={"space-between"}
                      spacing={1}
                    >
                      <Stack direction={"row"} spacing={1}>
                        <ListItemIcon style={{ minWidth: "38px" }}>
                          {user?.photo ? (
                            <Avatar src={user?.photo} alt="user photo" />
                          ) : (
                            <Avatar src={user?.photo} alt="user photo">
                              {`${user?.firstName?.substring(
                                0,
                                1
                              )}${user?.lastName?.substring(0, 1)}`}
                            </Avatar>
                          )}
                        </ListItemIcon>
                        <Stack direction={"column"} spacing={0}>
                          <span style={styles.userName}>
                            {user?.firstName} {user?.lastName}
                          </span>
                          <span style={styles.userTitle}>
                            {`${t("administrator")} PlayerDex`}
                          </span>
                        </Stack>
                      </Stack>
                      <Tooltip title={t("logout")}>
                        <div>
                          <LogoutIcon />
                        </div>
                      </Tooltip>
                    </Stack>
                  </ListItemButton>
                </ListItem>
              </List>
            </div>
          </Drawer>
        </Box>
        <ClubDetachConfirmationModal
          open={detachClubModal}
          onClose={() => setDetachClubModal(false)}
          onDetachClub={handleDetachClub}
          clubName={club?.name}
        />
        {/* <Toolbar /> */}
        <Suspense fallback={<Loader />}>{children}</Suspense>
      </Box>
    </LocalizationProvider>
  );
};

const styles = {
  link: {
    color: Grey[500],
    ...md.medium,
    textDecoration: "none",
  },
  selectedLink: {
    color: Base.PlayerDex,
    ...md.medium,
    textDecoration: "none",
  },
  searchFieldContainer: {
    padding: "0 24px",
    width: "100%",
  },
  searchFieldIcon: {
    borderBottomLeftRadius: "8px",
    borderTopLeftRadius: "8px",
    backgroundColor: Grey[800],
    height: "40px",
  },
  searchField: {
    backgroundColor: Grey[800],
    color: Grey[500],
    borderRadius: "8px",
    marginTop: "24px",
    marginBottom: "24px",
    border: "none",
    height: "40px",
    ...md.normal,
  },
  clubsField: {
    backgroundColor: Grey[800],
    color: Grey[500],
    borderRadius: "8px",
    marginTop: 0,
    marginBottom: "24px",
    border: "none",
    height: "40px",
    ...md.normal,
  },
  detachIcon: {
    color: Grey[500],
    marginTop: "-24px",
  },
  userName: {
    ...sm.medium,
    color: Base.White,
  },
  userTitle: {
    ...sm.normal,
    color: Grey[500],
  },
};

export default AppShell;
